/**
 * SprintPage — Vue Sprint Actif (utilisateurs internes uniquement)
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import backendApi from '../../services/backendApi';
import { ChevronDown } from 'lucide-react';
import { buildColorMap, computeBurndown } from '../../utils/dashboardMetrics';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

import SprintBurndownChart from './SprintBurndownChart';
import SpPipelineChart from './SpPipelineChart';
import ClientProgressChart from './ClientProgressChart';
import DevPerformanceChart from './DevPerformanceChart';
import MilestonesByTicketsChart from './MilestonesByTicketsChart';

const ProjectFilter = ({ projects, selectedId, onChange }) => (
  <div className="relative">
    <select
      value={selectedId ?? ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
      className="appearance-none bg-white border border-gray-200 rounded-xl px-4 py-2.5 pr-10
                 text-sm font-medium text-gray-700 shadow-sm cursor-pointer
                 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400
                 hover:border-gray-300 transition-colors"
    >
      <option value="">Tous les projets</option>
      {projects.map(p => (
        <option key={p.id} value={p.id}>
          {p.name}{p.partnerName ? ` — ${p.partnerName}` : ''}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
  </div>
);

const SprintPage = () => {
  const { isInternalUser } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [sprintData, setSprintData] = useState(null);
  const [burndownLoading, setBurndownLoading] = useState(true);
  const [devData, setDevData] = useState({ devPerformance: [], devNames: [], dailyData: [] });
  const [devLoading, setDevLoading] = useState(true);
  // Jalons : on réutilise la liste complète (overview non-sprint), comme la page Analyse Backlog
  const [backlogMilestones, setBacklogMilestones] = useState([]);

  useEffect(() => {
    if (!isInternalUser) navigate('/dashboard/tickets', { replace: true });
  }, [isInternalUser, navigate]);

  const fetchDevPerformance = useCallback((projectId) => {
    if (!isInternalUser) return;
    setDevLoading(true);
    backendApi.getDevPerformance(true, projectId)
      .then(dev => setDevData({
        devPerformance: dev.devPerformance || [],
        devNames: dev.devNames || [],
        dailyData: dev.dailyData || []
      }))
      .catch(() => {})
      .finally(() => setDevLoading(false));
  }, [isInternalUser]);

  const fetchAll = useCallback(() => {
    if (!isInternalUser) return;
    setLoading(true);
    setError(null);
    Promise.all([
      backendApi.getAnalyticsOverview(true),
      backendApi.getSprintBurndown(),
      backendApi.getDevPerformance(true, null),
      backendApi.getAnalyticsOverview(false)
    ]).then(([overview, burndown, dev, backlogOverview]) => {
      setData(overview);
      setSprintData(burndown);
      setDevData({
        devPerformance: dev.devPerformance || [],
        devNames: dev.devNames || [],
        dailyData: dev.dailyData || []
      });
      setBacklogMilestones(backlogOverview.milestones || []);
    }).catch(err => setError(err.message))
      .finally(() => {
        setLoading(false);
        setBurndownLoading(false);
        setDevLoading(false);
      });
  }, [isInternalUser]);

  useAutoRefresh(fetchAll, 5 * 60 * 1000);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Re-fetch dev performance quand le projet sélectionné change
  useEffect(() => {
    fetchDevPerformance(selectedProjectId);
  }, [selectedProjectId, fetchDevPerformance]);

  const filtered = useMemo(() => {
    if (!data) return { usStageGroups: [], tickets: [], milestones: [] };
    const { usStageGroups, tickets, milestones } = data;
    if (!selectedProjectId) return { usStageGroups, tickets, milestones };
    return {
      usStageGroups: usStageGroups.filter(g => g.projectId === selectedProjectId),
      tickets: tickets.filter(t => t.projectId === selectedProjectId),
      milestones: milestones.filter(m => m.projectId === selectedProjectId),
    };
  }, [data, selectedProjectId]);

  // Jalons : liste complète (comme le backlog), filtrée par projet sélectionné
  const milestonesData = useMemo(() => {
    if (!selectedProjectId) return backlogMilestones;
    return backlogMilestones.filter(m => m.projectId === selectedProjectId);
  }, [backlogMilestones, selectedProjectId]);

  // Pipeline SP : même population que le burndown, filtrable par projet
  const pipelineGroups = useMemo(() => {
    const groups = sprintData?.pipeline || [];
    if (!selectedProjectId) return groups;
    return groups.filter(g => g.projectId === selectedProjectId);
  }, [sprintData, selectedProjectId]);

  // Burndown : recalculé pour le projet sélectionné (sinon vue globale)
  const burndownData = useMemo(() => {
    if (!sprintData) return null;
    if (!selectedProjectId) return sprintData;
    const tasks = (sprintData.tasks || []).filter(t => t.projectId === selectedProjectId);
    return computeBurndown(tasks, sprintData.sprintStart, sprintData.sprintEnd);
  }, [sprintData, selectedProjectId]);

  const clientColorMap = useMemo(() => {
    const allProjects = data?.projects || [];
    const names = [
      ...allProjects.map(p => p.partnerName || p.name),
      ...(filtered.tickets || []).map(t => t.clientName)
    ];
    return buildColorMap(names);
  }, [data, filtered.tickets]);

  if (!isInternalUser) return null;

  // Seuls les projets présents dans le sprint actif (population du burndown/pipeline)
  const sprintProjectIds = new Set((sprintData?.pipeline || []).map(g => g.projectId).filter(Boolean));
  const projects = (data?.projects || [])
    .filter(p => sprintProjectIds.has(p.id))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-8 pt-8">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 text-sm">
          Erreur : {error}
        </div>
      </div>
    );
  }

  const { usStageGroups } = filtered;

  return (
    <div className="px-8 pb-8 space-y-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sprint Actif</h1>
          <p className="text-sm text-gray-500 mt-1">
            {selectedProjectId
              ? `Projet : ${projects.find(p => p.id === selectedProjectId)?.name}`
              : `Vue globale — ${projects.length} projets en cours`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ProjectFilter
            projects={projects}
            selectedId={selectedProjectId}
            onChange={setSelectedProjectId}
          />
        </div>
      </div>

      {/* Row 1 : Burndown (gauche) + Pipeline SP (droite, même population/filtres) */}
      {burndownLoading ? (
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
        </div>
      ) : (
        <div data-tour="sprint-charts" className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-2">
            <SprintBurndownChart sprintData={burndownData} />
          </div>
          <div className="lg:col-span-1">
            <SpPipelineChart usStageGroups={pipelineGroups} sprintActif={true} />
          </div>
        </div>
      )}

      {/* Row 3 : Progression par client + Performances développeurs */}
      <div data-tour="sprint-team" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ClientProgressChart
          usStageGroups={usStageGroups}
          projects={projects}
          sprintActif={true}
          colorMap={clientColorMap}
        />

        {devLoading ? (
          <div className="flex items-center justify-center h-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <DevPerformanceChart
            devPerformance={devData.devPerformance}
            devNames={devData.devNames}
            dailyData={devData.dailyData}
            sprintActif={true}
          />
        )}
      </div>

      {/* Row 4 : Jalons (liste complète + pagination, comme la page Analyse Backlog) */}
      <MilestonesByTicketsChart milestones={milestonesData} />
    </div>
  );
};

export default SprintPage;
